import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import api from '../../services/api';

interface SyncRun {
  id: number;
  mode: 'dry_run' | 'manual' | 'scheduled';
  status: string;
  requested_count: number;
  processed_count: number;
  created_count: number;
  updated_count: number;
  skipped_count: number;
  error_count: number;
  started_at: string;
  finished_at: string | null;
}

interface ErpStatus {
  configured: boolean;
  baseUrl: string | null;
  writesEnabled: boolean;
  maxBatch: number;
  recentRuns: SyncRun[];
}

interface PreviewResult {
  runId: number;
  status: string;
  processedCount: number;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  errorCount: number;
  details: Array<{ sku: string; action: string; status: string; message?: string | null }>;
}

const Container = styled.section`
  max-width: 980px;
  margin: 0 auto;
  padding: 24px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.06);
  padding: 24px;
  margin-bottom: 20px;
`;

const Status = styled.span<{ $ok: boolean }>`
  display: inline-block;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  color: ${({ $ok }) => ($ok ? '#166534' : '#991b1b')};
  background: ${({ $ok }) => ($ok ? '#dcfce7' : '#fee2e2')};
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 104px;
  box-sizing: border-box;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 12px;
  font: inherit;
  resize: vertical;
`;

const Button = styled.button<{ $primary?: boolean }>`
  border: 0;
  border-radius: 8px;
  padding: 10px 16px;
  color: ${({ $primary }) => ($primary ? '#fff' : '#1e3a8a')};
  background: ${({ $primary }) => ($primary ? '#1e3a8a' : '#e8eefc')};
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th, td {
    text-align: left;
    padding: 10px 8px;
    border-bottom: 1px solid #e5e7eb;
  }
`;

function parseSkus(value: string) {
  return [...new Set(value.split(/[\s,;]+/).map((item) => item.trim()).filter(Boolean))];
}

function formatDate(value: string | null) {
  return value ? new Date(value).toLocaleString('es-PE') : '-';
}

export default function ErpSyncTab() {
  const [status, setStatus] = useState<ErpStatus | null>(null);
  const [skus, setSkus] = useState('21333');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<PreviewResult | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      const response = await api.get<ErpStatus>('/products/erp/status');
      setStatus(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'No se pudo cargar el estado del ERP.');
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const execute = async (write: boolean) => {
    const selectedSkus = parseSkus(skus);
    if (!selectedSkus.length) {
      toast.error('Ingresa al menos un SKU.');
      return;
    }
    if (status && selectedSkus.length > status.maxBatch) {
      toast.error(`El máximo por lote es ${status.maxBatch} SKU.`);
      return;
    }
    if (write && !window.confirm('Se actualizarán únicamente los SKU revisados. ¿Continuar?')) return;

    setBusy(true);
    try {
      const response = await api.post<PreviewResult>(
        write ? '/products/erp/sync' : '/products/erp/preview',
        write ? { skus: selectedSkus, confirmation: 'SYNC' } : { skus: selectedSkus },
      );
      setResult(response.data);
      toast.success(write ? 'Sincronización registrada.' : 'Prueba en seco completada.');
      await loadStatus();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'No se pudo completar la operación ERP.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container>
      <h2>Sincronización ERP</h2>
      <p>Primero revisa en seco. No se actualizan imágenes ni se crean productos con una categoría ERP sin equivalencia aprobada.</p>

      <Card>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: 0 }}>Conexión</h3>
            <p style={{ marginBottom: 0 }}>{status?.baseUrl || 'URL ERP no configurada'}</p>
          </div>
          <Status $ok={Boolean(status?.configured)}>{status?.configured ? 'Configurada' : 'Falta token válido'}</Status>
        </div>
        <p style={{ marginTop: 16, marginBottom: 0 }}>
          Escrituras: <strong>{status?.writesEnabled ? 'habilitadas para el piloto' : 'bloqueadas por seguridad'}</strong>
        </p>
      </Card>

      <Card>
        <h3>Prueba de SKU</h3>
        <p>Ingresa SKU separados por coma, espacio o salto de línea. Máximo: {status?.maxBatch || 50}.</p>
        <Textarea value={skus} onChange={(event) => setSkus(event.target.value)} aria-label="SKU ERP" />
        <div style={{ display: 'flex', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
          <Button $primary onClick={() => execute(false)} disabled={busy || !status?.configured}>{busy ? 'Procesando…' : 'Ejecutar prueba en seco'}</Button>
          {status?.writesEnabled && <Button onClick={() => execute(true)} disabled={busy || !status.configured}>Sincronizar lote revisado</Button>}
        </div>
      </Card>

      {result && <Card>
        <h3>Resultado #{result.runId}</h3>
        <p>Procesados: {result.processedCount} · Crear: {result.createdCount} · Actualizar: {result.updatedCount} · Omitidos: {result.skippedCount} · Errores: {result.errorCount}</p>
        <Table>
          <thead><tr><th>SKU</th><th>Acción</th><th>Estado</th><th>Detalle</th></tr></thead>
          <tbody>{result.details.map((item, index) => <tr key={`${item.sku}-${index}`}><td>{item.sku}</td><td>{item.action}</td><td>{item.status}</td><td>{item.message || '-'}</td></tr>)}</tbody>
        </Table>
      </Card>}

      <Card>
        <h3>Últimas ejecuciones</h3>
        <Table>
          <thead><tr><th>ID</th><th>Modo</th><th>Estado</th><th>Procesados</th><th>Errores</th><th>Inicio</th></tr></thead>
          <tbody>{status?.recentRuns?.map((run) => <tr key={run.id}><td>{run.id}</td><td>{run.mode}</td><td>{run.status}</td><td>{run.processed_count}/{run.requested_count}</td><td>{run.error_count}</td><td>{formatDate(run.started_at)}</td></tr>)}</tbody>
        </Table>
      </Card>
    </Container>
  );
}
